import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { Badge } from "@/components/Badge";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { STAGE_COLORS } from "@/lib/constants";
import { formatDateTime } from "@/lib/dates";
import { ApiError } from "@/api/client";
import { getClienteRete, isAccessoSospeso } from "@/api/rete";
import type { DettaglioClienteAgenzia } from "@/types";
import { AccessoSospeso } from "./AccessoSospeso";

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="break-words">
      <span className="text-muted-foreground">{label}:</span>{" "}
      <span className="text-text">{value ?? "—"}</span>
    </div>
  );
}

const TORNA = (
  <Link to="/rete/clienti" className="inline-flex items-center gap-2 min-h-[44px] text-sm text-muted-foreground hover:text-text">
    <ArrowLeft size={16} /> Torna ai clienti
  </Link>
);

/** Dettaglio in sola lettura: niente privacy-link, niente documento (restano all'agenzia). */
export function DettaglioClienteRete() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DettaglioClienteAgenzia | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sospeso, setSospeso] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    getClienteRete(id)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        if (isAccessoSospeso(e)) setSospeso(true);
        else if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError("Impossibile caricare il cliente.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (sospeso) return <AccessoSospeso />;
  if (loading) return <div className="p-6 text-sm text-muted-foreground">Caricamento...</div>;
  if (notFound) {
    return (
      <div className="space-y-4">
        {TORNA}
        <EmptyState icon={<Search size={26} className="text-brand" />} title="Cliente non trovato" />
      </div>
    );
  }
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  const fullName = [data.first_name, data.last_name].filter(Boolean).join(" ") || "—";

  return (
    <div className="space-y-4">
      {TORNA}
      <h2 className="text-lg font-semibold text-text">{fullName}</h2>

      <div className="bg-card p-4 rounded-lg border border-border">
        <h3 className="font-semibold text-text mb-3">Anagrafica</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Field label="Email" value={data.email} />
          <Field label="Telefono" value={data.phone} />
          <Field label="Codice fiscale" value={data.codice_fiscale} />
          <Field label="IBAN" value={data.iban} />
          <Field label="Indirizzo" value={data.indirizzo} />
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <h3 className="font-semibold text-text px-4 pt-4 pb-3">Pratiche</h3>
        {data.pratiche.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-muted-foreground">Nessuna pratica</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pratica</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="hidden md:table-cell">Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.pratiche.map((p) => {
                  const color = STAGE_COLORS[p.stage] ?? STAGE_COLORS["Bozza"];
                  return (
                    <TableRow key={p.id} className="[&_td]:py-3">
                      <TableCell>
                        <Link
                          to={`/rete/pratiche/${encodeURIComponent(p.id)}`}
                          className="font-medium text-text hover:text-brand hover:underline"
                        >
                          {p.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge bg={color.bg} text={color.text}>{p.stage}</Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell tabular-nums text-muted-foreground">
                        {formatDateTime(p.created_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
